import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Container, Typography, Alert, Button } from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { applyActionCode, onAuthStateChanged } from "firebase/auth";
import { useIntl } from "react-intl";
import { auth } from "../../config/firebaseConfig";
import { signUp } from "../../api/companyApi";

const SIGNUP_STORAGE_KEY = "pendingSignUp";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const [status, setStatus] = useState("loading"); // "loading" | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const oobCode = searchParams.get("oobCode");

    if (oobCode) {
      // ── フロー A: カスタムアクションURL ──
      // Firebase Console のカスタムアクションURLがこのページを指している場合。
      // メールリンクを直接クリックするとここに来て oobCode が付いてくる。
      applyActionCode(auth, oobCode)
        .then(() => completeSignUp())
        .catch((err) => {
          console.error("applyActionCode error:", err);
          setStatus("error");
          setErrorMsg(err.message || t("auth.verify.failed"));
        });
    } else {
      // ── フロー B: Firebase デフォルトハンドラー経由 ──
      // Firebase の標準ページでメール確認済み → continueUrl でここにリダイレクトされる。
      // oobCode はなく、Firebase がすでに emailVerified = true にしている。
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (!user) {
          setStatus("error");
          setErrorMsg(t("auth.verify.noCode"));
          return;
        }
        // emailVerified の最新状態を取得
        await user.reload();
        if (user.emailVerified) {
          unsubscribe();
          completeSignUp();
        } else {
          setStatus("error");
          setErrorMsg(t("auth.verify.notVerified"));
        }
      });
      return () => unsubscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completeSignUp = async () => {
    const pending = localStorage.getItem(SIGNUP_STORAGE_KEY);
    if (!pending) {
      setStatus("error");
      setErrorMsg(t("auth.verify.noData"));
      return;
    }
    try {
      const signUpData = JSON.parse(pending);
      await signUp(signUpData);
      localStorage.removeItem(SIGNUP_STORAGE_KEY);
      setStatus("success");
      setTimeout(() => navigate("/signin", { replace: true }), 3000);
    } catch (err) {
      console.error("signUp error:", err);
      setStatus("error");
      setErrorMsg(err.message || t("auth.verify.failed"));
    }
  };

  return (
    <Container maxWidth="xs">
      <Box
        sx={{
          mt: 12,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Typography component="h1" variant="h5">
          {t("auth.verify.title")}
        </Typography>

        {status === "loading" && <CircularProgress />}

        {status === "success" && (
          <Alert severity="success" sx={{ width: "100%" }}>
            {t("auth.verify.success")}
          </Alert>
        )}

        {status === "error" && (
          <>
            <Alert severity="error" sx={{ width: "100%" }}>
              {errorMsg}
            </Alert>
            <Button variant="outlined" onClick={() => navigate("/signup")}>
              {t("auth.signup.title")}
            </Button>
          </>
        )}
      </Box>
    </Container>
  );
}
